"use client";

import {
  Field,
  NextImage,
  withDatasourceCheck,
  useComponentProps,
  GetServerSideComponentProps,
  ImageField,
  LinkField,
} from "@sitecore-jss/sitecore-jss-nextjs";
import clsx from "clsx";
import React, { useRef, useEffect } from "react";
import { buildExpressRequestPagesRouter } from "@repo/sitecore-lib/store-presentation";
import * as Flex from "@repo/ui/flex";
import * as Viewport from "@/components/viewport";
import { ComponentProps } from "../../types/component-props";
import { appendRef, detectDeviceType } from "../utils";
import { VideoControls } from "../video-controls";
import TextComponent from "./text-component/text-container";
import { GradientType } from "./utils";
import classes from "./media-cta.module.css";

export type MediaCtaFieldsProps = {
  id?: string;
  enableSound: Field<boolean> | { jsonValue: Field<boolean> };
  gradient: Field<string>;
  textAlignment?: Field<string>;
  headline: Field<string>;
  subheading: Field<string>;
  textColour: Field<string>;
  mobileImage: ImageField;
  desktopImage: ImageField;
  mobileVideo: Field<string>;
  desktopVideo: Field<string>;
  ctaLink: LinkField | { jsonValue: LinkField };
  ctaText: Field<string>;
  ctaRef: Field<string>;
  mobileTextAlignment?: Field<string>;
  desktopTextAlignment?: Field<string>;
  displayGradientOnlyOnHover: Field<boolean> | { jsonValue: Field<boolean> };
  deviceTargeting?: Field<string>;
};

export type MediaCtaProps = ComponentProps & {
  isActive?: boolean;
  id?: string;
  fields: MediaCtaFieldsProps;
};

type ServerData = {
  platform: "mobile" | "desktop";
};

const shouldRender = (
  platform: string | undefined,
  targetDevice: string | undefined,
) => {
  if (!targetDevice) return true;
  if (!platform) return true;
  return platform === targetDevice;
};

export const getFieldValue = <T,>(
  field: Field<T> | { jsonValue: Field<T> },
): T => ("jsonValue" in field ? field.jsonValue.value : field.value);

const MediaCta = (props: MediaCtaProps) => {
  const {
    id,
    isActive,
    fields: {
      id: ctaId,
      enableSound,
      desktopImage,
      mobileImage,
      desktopVideo,
      mobileVideo,
      headline,
      ctaText,
      ctaLink,
      ctaRef,
      subheading,
      textAlignment,
      mobileTextAlignment,
      desktopTextAlignment,
      gradient,
      textColour,
      displayGradientOnlyOnHover,
      deviceTargeting,
    },
    rendering: { componentName },
  } = props;

  const data = useComponentProps<ServerData>(props.rendering.uid);
  const mobileVideoRef = useRef<HTMLVideoElement>(null);
  const desktopVideoRef = useRef<HTMLVideoElement>(null);
  const platform = data?.platform;
  const targetedDevice = deviceTargeting?.value.toLocaleLowerCase() ?? "";
  if (!shouldRender(platform, targetedDevice)) {
    return <></>;
  }

  const isHero = !componentName.match(/cta/i);
  const hasVideo = desktopVideo.value || mobileVideo.value;

  useEffect(() => {
    mobileVideoRef.current?.play().catch(() => {});
    desktopVideoRef.current?.play().catch(() => {});
  }, [hasVideo, isActive]);

  const renderMediaCta = (isMobileView: boolean) => {
    const mediaImage = isMobileView ? mobileImage : desktopImage;
    const mediaVideo = isMobileView ? mobileVideo : desktopVideo;
    const videoRef = isMobileView ? mobileVideoRef : desktopVideoRef;
    const viewportKey = `${id || ctaId}-${isMobileView ? "m" : "d"}`;

    const gradientValue = gradient?.value.toLocaleLowerCase() as GradientType;

    const showGradientOnHover =
      getFieldValue(displayGradientOnlyOnHover) ?? false;
    const linkHref = getFieldValue(ctaLink)?.href ?? "#";
    const ctaLinkWithRef = appendRef(linkHref, ctaRef.value);

    const gradientOptions = {
      "all overlays": showGradientOnHover ? "overlayWithHover" : "overlay",
      "layer overlay": "layerOverlay",
      "no overlays": "",
    };

    const carouselAccessibilityProps = {
      "aria-hidden": !isActive,
      tabIndex: isActive ? 0 : -1,
      "data-label": "carousel-slide",
    };

    const mediaCtaAccessibilityProps = {
      tabIndex: 0,
      "aria-label": ctaText.value || "Learn more",
    };

    const accessibility =
      componentName === "Hero Carousel"
        ? carouselAccessibilityProps
        : mediaCtaAccessibilityProps;

    return (
      <a
        {...accessibility}
        id={id || ctaId || ""}
        href={ctaLinkWithRef}
        style={{ cursor: linkHref ? "pointer" : "default" }}
        data-testid="media-container"
        className={clsx(
          classes.mediaContainer,
          isMobileView && classes.visible,
          gradientValue && classes[gradientOptions[gradientValue]!],
          !isMobileView && isHero && classes.heroMediaContainer,
        )}
      >
        {mediaVideo.value ? (
          <video
            key={`video-${viewportKey}`}
            autoPlay
            muted
            loop
            playsInline
            ref={videoRef}
            className={classes.media}
            src={mediaVideo.value}
            data-active={isActive}
            data-id={id || ctaId || ""}
            data-skip-carousel-focus="true"
            tabIndex={-1}
          ></video>
        ) : (
          <NextImage
            field={mediaImage}
            className={classes.media}
            alt={mediaImage?.value?.alt as string}
            unoptimized={true}
          />
        )}
        <Flex.Container
          column
          className={
            classes[
              isMobileView || !isHero
                ? "ctaTextContainerWrapper"
                : "heroCtaTextContainerWrapper"
            ]
          }
        >
          <TextComponent
            textAlignment={textAlignment}
            headline={headline}
            subheading={subheading}
            textColour={textColour}
            ctaText={ctaText}
            mobileTextAlignment={mobileTextAlignment}
            desktopTextAlignment={desktopTextAlignment}
            isHero={isHero}
            isMobileView={isMobileView}
          />
          {mediaVideo.value && (
            <VideoControls
              key={`controls-${viewportKey}`}
              hasSound={
                !!(
                  enableSound && "jsonValue" in enableSound
                    ? enableSound.jsonValue
                    : enableSound
                )?.value
              }
              size="lg"
              videoRef={videoRef}
              isActive={isActive as boolean}
              componentName={componentName}
            />
          )}
        </Flex.Container>
      </a>
    );
  };

  return (
    <>
      <Viewport.Small>{renderMediaCta(true)}</Viewport.Small>
      <Viewport.Large>{renderMediaCta(false)}</Viewport.Large>
    </>
  );
};

export const getServerSideProps: GetServerSideComponentProps = async (
  rendering,
  _layoutData,
  context,
) => {
  const req = context?.req;

  if (!req) return { platform: "" };

  try {
    const { request } = await buildExpressRequestPagesRouter(req);
    const platform = detectDeviceType(request.headers);

    return { platform };
  } catch (err) {
    console.log("Media | Hero component error:", { err });
    return { platform: "" };
  }
};

export default withDatasourceCheck()<MediaCtaProps>(MediaCta);
